import { ApolloClient, ApolloLink, InMemoryCache, NormalizedCacheObject } from '@apollo/client';
import { getConfigValue } from '@indigo-ag/ui-core';
import { createUploadLink } from 'apollo-upload-client';

import { getAuthMiddlewareLink } from './auth';
import { getErrorLink } from './error';

const isMockMode = getConfigValue('IA_API_MODE') === 'mock';
let liveClient: ApolloClient<NormalizedCacheObject>;

if (!isMockMode) {
  liveClient = new ApolloClient({
    cache: new InMemoryCache({
      typePolicies: {
        AgOrderInfo: {
          keyFields: ['opportunities']
        },
        // Using this as a reference: https://www.apollographql.com/docs/react/caching/cache-field-behavior/#merging-non-normalized-objects
        Phase3: {
          fields: {
            files: {
              merge: false
            }
          }
        },
        Query: {
          fields: {
            financingAgFinancingFiles: {
              merge: false
            }
          }
        },
        AgGroup: {
          fields: {
            agFinancingProcesses: {
              merge: false
            }
          }
        }
      }
    }),
    link: ApolloLink.from([
      getAuthMiddlewareLink(),
      getErrorLink(),
      createUploadLink({ uri: `${getConfigValue('API_ENDPOINT')}/graphql` })
    ]),
    assumeImmutableResults: true
  });
}

// supports the async loading of the mock API when not in production
export const getClient = (): Promise<ApolloClient<NormalizedCacheObject>> => {
  if (isMockMode) {
    // We leverage code-splitting in order to avoid bundling the mock-api in with our production asset.
    // The upside is that it reduces our bundle size by 400kb, the downside is async flow.
    return import('@indigo-ag/mock-api').then(mockApi => {
      // We're having some issues with typings with the mock-api.  Remove this `as any as ___` hack when
      // tslint:disable-next-line:no-any
      return (mockApi.getMockClient({ logger: console }) as any) as Promise<ApolloClient<NormalizedCacheObject>>;
    });
  }
  return (Promise.resolve(liveClient) as any) as Promise<ApolloClient<NormalizedCacheObject>>;
};
